// src/pages/CourseDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import Layout from '../Layout/Layout';
import PaymentModal from '../components/PaymentModal';
import api from '../utils/apiClient';
import {
    FiClock,
    FiUsers,
    FiCalendar,
    FiLayers,
    FiAward,
    FiCheckCircle,
    FiX,
    FiArrowRight,
  FiPlay,
  FiCode
} from 'react-icons/fi';

// Styled Components
const CourseContainer = styled.div`
  min-height: calc(100vh - 70px);
`;

const CourseHeaderSection = styled.section`
  padding: ${({ theme }) => theme.spacing[6]} 0;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  position: relative;
  overflow: hidden;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CourseImageContainer = styled.div`
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ gradient, theme }) => gradient || theme.colors.gradientPrimary};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

const CourseLetter = styled.div`
  font-size: 72px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.background};
`;

const CourseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const CourseInfo = styled.div`
  flex: 1;
`;

const CourseTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  }
`;

const DifficultyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  background-color: ${({ difficulty, theme }) => {
        switch (difficulty) {
            case 'beginner': return theme.colors.beginner;
            case 'intermediate': return theme.colors.intermediate;
            case 'advanced': return theme.colors.advanced;
            case 'expert': return theme.colors.expert;
            default: return theme.colors.beginner;
        }
    }};
  color: white;
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-right: ${({ theme }) => theme.spacing[3]};
`;

const CourseMetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Tag = styled.span`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.secondary};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
`;

const ActionButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: ${({ enrolled, theme }) =>
        enrolled
            ? 'transparent'
            : theme.colors.gradientPrimary};
  color: ${({ enrolled, theme }) =>
        enrolled
            ? theme.colors.primary
            : theme.colors.background};
  border: ${({ enrolled, theme }) =>
        enrolled
            ? `1px solid ${theme.colors.primary}`
            : 'none'};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-shadow: ${({ enrolled, theme }) =>
        enrolled
            ? 'none'
            : theme.shadows.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover {
    background-color: ${({ enrolled, theme }) =>
        enrolled
            ? `${theme.colors.primary}15`
            : ''};
    
    svg {
      transform: translateX(4px);
    }
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const SideContent = styled.div`
  @media (max-width: 992px) {
    order: -1;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 24px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    margin-right: ${({ theme }) => theme.spacing[3]};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const CourseDescription = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ModulesSection = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ModulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ModuleCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ModuleHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const ModuleTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModuleDetails = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const ModuleContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
`;

const LessonItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  
  &:last-child {
    border-bottom: none;
  }
`;

const LessonTitle = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// const LessonDuration = styled.div`
//   color: ${({ theme }) => theme.colors.textSecondary};
//   font-size: ${({ theme }) => theme.fonts.sizes.sm};
// `;

const NoModulesMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InfoCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  &::before {
    content: '';
    display: block;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    width: 100%;
  }
`;

const InfoCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  margin: 0;
  text-align: center;
`;

const InfoCardContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InfoValue = styled.span`
  font-weight: 500;
`;

const RequirementsList = styled.ul`
  padding-left: ${({ theme }) => theme.spacing[4]};
  margin: ${({ theme }) => theme.spacing[4]} 0;
`;

const RequirementItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  &::marker {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(10, 255, 112, 0.1);
  border-radius: 50%;
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
`;

const ErrorIcon = styled(FiX)`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 500;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}11;
  }
`;

const EnrolledBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.success}22;
  color: ${({ theme }) => theme.colors.success};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [openModules, setOpenModules] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Fetch course data
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await api.courses.getById(id);
        setCourse(data);

        // Initialize open state for modules
        if (data.modules && data.modules.length > 0) {
          const moduleStates = {};
          data.modules.forEach((module, index) => {
            moduleStates[module.id] = index === 0; // Open first module by default
          });
          setOpenModules(moduleStates);
        }
      } catch (err) {
        console.error('Error fetching course:', err);
        setError('Failed to load course. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  // Toggle module open/close
  const toggleModule = (moduleId) => {
    setOpenModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Handle enrollment
  const handleEnroll = async () => {
    // First check if course is free
    if (course && !course.is_free && !course.has_purchased) {
      // Paid course - show payment modal
      setShowPaymentModal(true);
      return;
    }

    // For free courses, proceed with direct enrollment
    try {
      setEnrolling(true);
      await api.courses.enroll(id);
      // Refresh course data to update enrollment status
      const updatedCourse = await api.courses.getById(id);
      setCourse(updatedCourse);
      toast.success('Successfully enrolled in course!');
    } catch (err) {
      console.error('Error enrolling in course:', err);

      // Handle payment required error
      if (err.response?.status === 402) {
        toast.error('This course requires payment. Please purchase the course first.');
        setShowPaymentModal(true);
      } else if (err.response?.data?.requires_payment) {
        toast.error(`Payment required for ${course.title}. Please purchase the course first.`);
        setShowPaymentModal(true);
      } else {
        toast.error('Failed to enroll in course. Please try again later.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  // Handle browsing the course (when already enrolled)
  const handleBrowseCourse = () => {
    navigate(`/learning/${id}`);
  };

  // Handle payment success
  const handlePaymentSuccess = async () => {
    try {
      // Refresh course data to update enrollment status
      const updatedCourse = await api.courses.getById(id);
      setCourse(updatedCourse);
      toast.success('Payment successful! You are now enrolled in the course.');
    } catch (error) {
      console.error('Error refreshing course data:', error);
      toast.success('Payment successful! Please refresh the page to see updated enrollment status.');
    }
  };

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading course details...</LoadingText>
        </LoadingContainer>
      </Layout>
    );
  }

  if (error || !course) {
    return (
      <Layout>
        <ErrorContainer>
          <ErrorIcon />
          <ErrorMessage>{error || 'Course not found'}</ErrorMessage>
          <BackButton to="/courses">Back to Courses</BackButton>
        </ErrorContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <CourseContainer>
        <CourseHeaderSection>
          <HeaderContent>
            <CourseImageContainer>
              {course.thumbnail ? (
                <CourseImage src={course.thumbnail} alt={course.title} />
              ) : (
                  <CourseLetter>
                  {course.title ? course.title[0] : 'C'}
                </CourseLetter>
              )}
            </CourseImageContainer>

            <CourseInfo>
              {course.enrolled && (
                <EnrolledBadge>
                  <FiCheckCircle /> Enrolled
                </EnrolledBadge>
              )}

              <CourseTitle>{course.title}</CourseTitle>

              <CourseMetaInfo>
                <DifficultyBadge difficulty={course.difficulty_level || 'beginner'}>
                  {course.difficulty_level || 'Beginner'}
                </DifficultyBadge>

                <MetaItem>
                  <FiClock />
                  {course.duration_text || `${course.estimated_duration || 0} hours`}
                </MetaItem>

                <MetaItem>
                  <FiUsers />
                  {course.registered_users || 0} enrolled
                </MetaItem>

                {course.updated_at && (
                  <MetaItem>
                    <FiCalendar />
                    Updated {formatDate(course.updated_at)}
                  </MetaItem>
                )}

                {course.certification_available && (
                  <MetaItem>
                    <FiAward />
                    Certificate Available
                  </MetaItem>
                )}
              </CourseMetaInfo>

              <TagsContainer>
                {course.skills_covered && course.skills_covered.length > 0 ?
                  course.skills_covered.map((skill, index) => (
                    <Tag key={index}>{skill}</Tag>
                  )) : null}
              </TagsContainer>

              <ActionButton
                onClick={course.enrolled ? handleBrowseCourse : handleEnroll}
                disabled={enrolling}
                enrolled={course.enrolled}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {course.enrolled ? (
                  <>Browse Course <FiPlay /></>
                ) : course.is_free ? (
                  <>
                    {enrolling ? 'Enrolling...' : 'Enroll Free'} <FiArrowRight />
                  </>
                ) : course.has_purchased ? (
                  <>
                    {enrolling ? 'Enrolling...' : 'Enroll Now'} <FiArrowRight />
                      </>
                    ) : (
                      <>
                        Purchase Course (₹{course.price}) <FiArrowRight />
                  </>
                )}
              </ActionButton>

            </CourseInfo>
          </HeaderContent>
        </CourseHeaderSection>

        <ContentContainer>
          <MainContent>
            <SectionTitle>Course Overview</SectionTitle>
            <CourseDescription>
              {course.description}
            </CourseDescription>

            <ModulesSection id="course-modules">
              <SectionTitle>Course Modules</SectionTitle>

              {course.modules && course.modules.length > 0 ? (
                <ModulesList>
                  {course.modules.map(module => (
                    <ModuleCard key={module.id}>
                      <ModuleHeader onClick={() => toggleModule(module.id)}>
                        <ModuleTitle>
                          <FiLayers /> {module.title}
                        </ModuleTitle>
                        <ModuleDetails>
                          <FiClock />
                          {module.duration_text || `${module.estimated_time ? module.estimated_time / 60 : 1} hour(s)`}
                        </ModuleDetails>
                      </ModuleHeader>

                      <ModuleContent isOpen={openModules[module.id]}>
                        <p>{module.description || 'No description available for this module.'}</p>

                      </ModuleContent>
                    </ModuleCard>
                  ))}
                </ModulesList>
              ) : (
                <NoModulesMessage>
                  No modules available for this course yet. Check back soon!
                </NoModulesMessage>
              )}
            </ModulesSection>
          </MainContent>

          <SideContent>
            <InfoCard>
              <InfoCardTitle>Course Information</InfoCardTitle>
              <InfoCardContent>
                <InfoItem>
                  <InfoLabel>Difficulty:</InfoLabel>
                  <InfoValue style={{
                    color: course.difficulty_level === 'beginner' ? '#00CC66' :
                      course.difficulty_level === 'intermediate' ? '#FFB82E' :
                        course.difficulty_level === 'advanced' ? '#FF3A5E' :
                          '#D740FF'
                  }}>
                    {course.difficulty_level || 'Not specified'}
                  </InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Duration:</InfoLabel>
                  <InfoValue>{course.duration_text || `${course.estimated_duration || 0} hours`}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Total Modules:</InfoLabel>
                  <InfoValue>{course.total_modules || 0}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Total Lessons:</InfoLabel>
                  <InfoValue>{course.total_lessons || 0}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Enrolled:</InfoLabel>
                  <InfoValue>{course.registered_users || 0}</InfoValue>
                </InfoItem>



                {course.created_at && (
                  <InfoItem>
                    <InfoLabel>Created:</InfoLabel>
                    <InfoValue>{formatDate(course.created_at)}</InfoValue>
                  </InfoItem>
                )}

                {course.updated_at && (
                  <InfoItem>
                    <InfoLabel>Updated:</InfoLabel>
                    <InfoValue>{formatDate(course.updated_at)}</InfoValue>
                  </InfoItem>
                )}
              </InfoCardContent>
            </InfoCard>

            <InfoCard>
              <InfoCardTitle>Prerequisites</InfoCardTitle>
              <InfoCardContent>
                {course.prerequisites && course.prerequisites.length > 0 ? (
                  <RequirementsList>
                    {course.prerequisites.map((prerequisite, index) => (
                      <RequirementItem key={index}>{prerequisite}</RequirementItem>
                    ))}
                  </RequirementsList>
                ) : (
                  <p>No specific prerequisites for this course.</p>
                )}
              </InfoCardContent>
            </InfoCard>

            <InfoCard>
              <InfoCardTitle>Skills You'll Learn</InfoCardTitle>
              <InfoCardContent>
                {course.skills_covered && course.skills_covered.length > 0 ? (
                  <RequirementsList>
                    {course.skills_covered.map((skill, index) => (
                      <RequirementItem key={index}>{skill}</RequirementItem>
                    ))}
                  </RequirementsList>
                ) : (
                  <p>Skills will be listed soon.</p>
                )}
              </InfoCardContent>
            </InfoCard>
          </SideContent>
        </ContentContainer>
      </CourseContainer>

      {/* Payment Modal */}
      {course && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          course={course}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Layout>
  );
};

export default CourseDetail;